import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { apiError } from "@/lib/api";
import { Logo } from "@/components/Navbar";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function Label({ children, htmlFor }) {
  return <label htmlFor={htmlFor} className="label-mono mb-1.5 block">{children}</label>;
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(location.state?.error || "");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate(location.state?.from || "/dashboard");
    } catch (err) { setError(apiError(err, "Invalid email or password")); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-paper">
      {/* Editorial left side */}
      <aside className="hidden lg:flex flex-col justify-between p-12 bg-persimmon text-white relative overflow-hidden">
        <div className="absolute inset-0 dots-persimmon opacity-100" aria-hidden />
        <div className="absolute top-12 right-12 w-24 h-24 bg-butter rotate-12" aria-hidden />
        <div className="relative">
          <Logo />
        </div>
        <div className="relative max-w-md">
          <div className="label-mono text-white/70">№ 01 · Recent verdict</div>
          <h2 className="mt-6 font-display font-bold text-4xl leading-[1.05] tracking-tightest">
            "We went from a 58 SEO score to 89 in an afternoon. The recommendations were specific, not generic AI fluff."
          </h2>
          <div className="mt-6 label-mono text-white/80">— Priya R. · Home goods FBA seller</div>
        </div>
        <div className="relative label-mono text-white/60">© {new Date().getFullYear()} · CliqCommerce</div>
      </aside>

      {/* Form right side */}
      <main className="flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo /></div>
          <div className="label-mono">Sign in</div>
          <h1 className="mt-3 font-display font-black text-4xl md:text-5xl tracking-tightest text-ink leading-[1]">Welcome back.</h1>
          <p className="text-ink/60 mt-3">Log in to run the analysts.</p>

          <Button
            variant="outline"
            type="button"
            className="w-full mt-8 h-11"
            onClick={googleLogin}
            data-testid="google-login-btn"
          >
            <GoogleIcon /> Continue with Google
          </Button>

          <div className="flex items-center gap-3 my-6 label-mono">
            <div className="h-px bg-rule flex-1" />OR<div className="h-px bg-rule flex-1" />
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="login-email">Email</Label>
              <Input id="login-email" data-testid="login-email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="login-password">Password</Label>
              <Input id="login-password" data-testid="login-password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} required />
            </div>
            {error && (
              <div className="text-sm text-alert border border-alert/30 bg-alert-bg px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}
            <Button type="submit" disabled={loading} className="w-full h-11" data-testid="login-submit">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <p className="text-sm text-ink/60 mt-6">
            New here?{" "}
            <Link to="/signup" className="font-medium text-ink underline underline-offset-4 hover:text-ink-2" data-testid="login-signup-link">
              Create an account →
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4 mr-2"><path fill="#111" d="M12 11v3.2h7.3c-.3 1.7-2 4.9-7.3 4.9-4.4 0-8-3.6-8-8s3.6-8 8-8c2.5 0 4.2 1 5.2 2l3.5-3.3C18.5 1 15.5 0 12 0 5.4 0 0 5.4 0 12s5.4 12 12 12c6.9 0 11.5-4.9 11.5-11.7 0-.8-.1-1.4-.2-2H12z"/></svg>
  );
}
